import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Search, User, ArrowUp, ArrowDown, ArrowRight, Download, ChevronDown, BarChart2, Shield, AlertTriangle, FileText, AlertCircle, PlusCircle, TrendingUp, CheckCircle2, Clock, Menu, Bell, Settings, ChevronRight, Play, Send, ChevronLeft, Info, Calendar, X } from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts'
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate, useNavigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { Toaster } from 'react-hot-toast'
import UpdatePage from './pages/UpdatePage'
import AIChat from './components/AIChat'
import logoIcon from './assets/logo3.png'
import './App.css'
import AssessmentWizard from './components/AssessmentWizard'
import Dashboard from './pages/Dashboard'
import LiveRemediationActions from './pages/LiveRemediationActions'
import Analytics from './pages/Analytics'
import Auth from './pages/Auth'
import ComingSoon from './pages/ComingSoon'
import ProtectedRoute from './components/ProtectedRoute'
import wizardService from './services/wizardService'
import Wizard from './components/Wizard'
import { Menu as MenuIcon } from 'lucide-react'
import AiTrustRatingIndex from './pages/AiTrustRatingIndex'

// Placeholder data
const portfolioData = [
  { company: 'LucidBlack', rating: 'AAA', date: 'Jun 1, 2025', trend: 'up', risk: 'Low', exposure: '$2.5M' },
  { company: 'QuantifAI', rating: 'AA', date: 'May 29, 2025', trend: 'stable', risk: 'Medium', exposure: '$1.8M' },
  { company: 'FintechX', rating: 'BBB', date: 'May 20, 2025', trend: 'down', risk: 'Medium', exposure: '$3.2M' },
  { company: 'DataGuard', rating: 'B', date: 'May 15, 2025', trend: 'down', risk: 'High', exposure: '$1.5M' },
]

const performanceData = [
  { month: 'Jan', rating: 90 },
  { month: 'Feb', rating: 85 },
  { month: 'Mar', rating: 88 },
  { month: 'Apr', rating: 92 },
  { month: 'May', rating: 95 },
  { month: 'Jun', rating: 98 },
]

const riskData = [
  { subject: 'Regulatory Compliance', A: 85, B: 90 },
  { subject: 'Cybersecurity', A: 90, B: 85 },
  { subject: 'AI Ethics', A: 75, B: 80 },
  { subject: 'Data Quality', A: 80, B: 75 },
  { subject: 'Transparency', A: 70, B: 85 },
]

const stats = {
  totalCompanies: 10,
  avgRating: 'AA',
  highRiskCompanies: 2,
}

const trendData = [
  { month: 'Jan', rating: 75, baseline: 65 },
  { month: 'Feb', rating: 78, baseline: 66 },
  { month: 'Mar', rating: 82, baseline: 67 },
  { month: 'Apr', rating: 85, baseline: 68 },
  { month: 'May', rating: 88, baseline: 69 },
  { month: 'Jun', rating: 92, baseline: 70 },
]

const recentRatings = [
  { id: 1, name: 'AI Model A', rating: 90, date: '2024-03-15' },
  { id: 2, name: 'AI Model B', rating: 75, date: '2024-03-10' },
  { id: 3, name: 'AI Model C', rating: 85, date: '2024-03-05' },
]

const pendingAssessments = [
  { id: 1, name: 'AI Model D', dueDate: '2024-03-25' },
  { id: 2, name: 'AI Model E', dueDate: '2024-03-30' },
]

const complianceSnapshot = {
  score: 85,
  status: 'Compliant',
  lastUpdated: 'May 28, 2025'
};

const aiEnablementData = [
  {
    tool: "Coding Assistant",
    examples: "GitHub Copilot/Codeium/Codey",
    adoption: 75,
    status: "enabled",
    tip: "+20% dev velocity"
  },
  {
    tool: "AI Code Review",
    examples: "Amazon CodeGuru, DeepCode",
    adoption: 30,
    status: "partial",
    tip: "Reduces bugs by 25%"
  },
  {
    tool: "AI Writing Assistant",
    examples: "ChatGPT Teams, Jasper",
    adoption: 45,
    status: "partial",
    tip: "Cuts doc time by 35%"
  },
  {
    tool: "AI Design / Image Gen",
    examples: "Midjourney, Firefly, DALL·E",
    adoption: 0,
    status: "missing",
    tip: "Speeds up asset creation"
  },
  {
    tool: "Automated Documentation",
    examples: "Mintlify AI, Swimm AI Doc, OpenAI GPT doc bot",
    adoption: 55,
    status: "partial",
    tip: "Reduces stale docs, helps onboarding"
  },
  {
    tool: "AI Meeting & On‑boarding Assistants",
    examples: "Otter.ai, Fireflies.ai, Loom AI, GuidePilot",
    adoption: 80,
    status: "enabled",
    tip: "Cuts ramp‑up time for new hires; searchable meeting notes"
  },
  {
    tool: "AI Data Exploration",
    examples: "ThoughtSpot AI, Mode AI Notebooks, Hex Magic AI",
    adoption: 25,
    status: "partial",
    tip: "Self‑serve analytics; quicker insights for PMs & execs"
  },
  {
    tool: "AI Agent Frameworks",
    examples: "LangChain, CrewAI, AutoGen, OpenAI GPT Actions",
    adoption: 65,
    status: "enabled",
    tip: "Foundation for internal chatbots / workflow agents"
  },
  {
    tool: "ML Ops Automation",
    examples: "DataRobot, Vertex AI Pipelines, Hugging Face AutoTrain",
    adoption: 15,
    status: "missing",
    tip: "Faster model iteration, consistent governance"
  }
];

const radarData = [
  { subject: 'Coding Assistant', A: 75, B: 65 },
  { subject: 'Documentation', A: 55, B: 70 },
  { subject: 'Meeting Assistant', A: 80, B: 60 },
  { subject: 'Data Exploration', A: 25, B: 45 },
  { subject: 'ML Ops', A: 15, B: 40 }
];

function MainLayout({ children, isWizardOpen, setIsWizardOpen }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showEducationalWizard, setShowEducationalWizard] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAnnouncement, setShowAnnouncement] = useState(true);

  useEffect(() => {
    const checkWizardStatus = async () => {
      try {
        console.log('🔍 Checking educational wizard status...');
        const status = await wizardService.getWizardStatus();
        console.log('✅ Educational wizard status:', status);
        
        if (!status.seen) {
          console.log('🔄 Opening educational wizard...');
          setShowEducationalWizard(true);
        }
      } catch (error) {
        console.error('❌ Error checking wizard status:', error);
      }
    };

    checkWizardStatus();
  }, []);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProfileMenu && !event.target.closest('.profile-menu-container')) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showProfileMenu]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background text-text">
      {/* Announcement (modern, clean, not a bar) */}
      {showAnnouncement && (
        <div className="flex justify-center mt-4">
          <div className="flex items-center gap-3 bg-primary/10 text-primary px-6 py-2 rounded-full shadow-sm font-medium text-sm">
            <span>Just Launched: Discover the AI Maturity Index for Fortune 100 Companies – Explore AI Trust Scores Now!</span>
            <button
              onClick={() => setShowAnnouncement(false)}
              className="p-1 rounded-full hover:bg-primary/20 transition-colors"
              aria-label="Close announcement"
              style={{ lineHeight: 0 }}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}
      <AIChat />
      <AssessmentWizard isOpen={isWizardOpen} onClose={() => setIsWizardOpen(false)} />
      <Wizard isOpen={showEducationalWizard} onClose={() => setShowEducationalWizard(false)} />
      <div className="ml-[400px] transition-all duration-300">
        {/* Navbar */}
        <nav className="sticky top-0 z-10 bg-background border-b border-primary/20">
          <div className="px-4 md:px-8 h-20 flex items-center justify-between">
            <div className="flex items-center gap-4 md:gap-8 pr-4 md:pr-8 flex-shrink-0">
              <Link to="/dashboard" className="flex items-center gap-3">
                <img src={logoIcon} alt="Quantifaid Logo" className="h-8 w-8" />
                <span className="logo-text text-2xl tracking-wider">Quantifaid</span>
              </Link>
            </div>
            {/* Desktop Menu */}
            <div className="hidden md:flex gap-4 whitespace-nowrap flex-1">
              <Link to="/dashboard" className="px-4 py-2 rounded-lg transition-colors hover:bg-primary/10">Dashboard</Link>
              <button className="px-4 py-2 rounded-lg transition-colors hover:bg-primary/10" onClick={() => setIsWizardOpen(true)}>Start New Assessment</button>
              <Link to="/live-remediation" className="px-4 py-2 rounded-lg transition-colors hover:bg-primary/10">Live Remediation</Link>
              <Link to="/analytics" className="px-4 py-2 rounded-lg transition-colors hover:bg-primary/10">Analytics</Link>
              <Link to="/ai-trust-index" className="px-4 py-2 rounded-lg transition-colors hover:bg-primary/10">
                AI Maturity Index
              </Link>
              <Link to="/sentiment" className="px-4 py-2 rounded-lg transition-colors relative hover:bg-primary/10">
                AI News
                <span className="absolute -top-2 -right-2 px-2 py-0.5 text-xs bg-warning text-background rounded-full text-[10px] font-medium">Coming Soon</span>
              </Link>
              <Link to="/sentiment" className="px-4 py-2 rounded-lg transition-colors relative hover:bg-primary/10">
                Sentiment Index
                <span className="absolute -top-2 -right-2 px-2 py-0.5 text-xs bg-warning text-background rounded-full text-[10px] font-medium">Coming Soon</span>
              </Link>
            </div>
            {/* Mobile Menu Button */}
            <div className="flex items-center gap-2 md:hidden">
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 rounded-lg hover:bg-primary/10 transition-colors">
                <MenuIcon size={28} />
              </button>
            </div>
            {/* User Info & Profile */}
            <div className="flex items-center gap-4">
              <span className="text-text-secondary hidden md:inline">{user?.name || user?.email}</span>
              <div className="profile-menu-container relative">
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowProfileMenu(!showProfileMenu);
                  }}
                  className="p-2 rounded-full hover:bg-primary/10 transition-colors"
                >
                  <User size={24} />
                </button>
                {/* Profile Dropdown Menu */}
                {showProfileMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-background border border-primary/20 rounded-lg shadow-lg py-1 z-50">
                    <button
                      onClick={logout}
                      className="w-full px-4 py-2 text-left text-text-secondary hover:bg-primary/10 transition-colors flex items-center gap-2"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Mobile Drawer */}
          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                className="fixed top-0 right-0 w-64 h-full bg-background border-l border-primary/20 z-50 shadow-2xl flex flex-col p-6 gap-4 md:hidden"
              >
                <button onClick={() => setMobileMenuOpen(false)} className="self-end mb-4 p-2 rounded-lg hover:bg-primary/10 transition-colors">
                  <X size={24} />
                </button>
                <Link to="/dashboard" className="px-4 py-2 rounded-lg transition-colors hover:bg-primary/10" onClick={() => setMobileMenuOpen(false)}>Dashboard</Link>
                <button className="px-4 py-2 rounded-lg transition-colors hover:bg-primary/10 text-left" onClick={() => { setIsWizardOpen(true); setMobileMenuOpen(false); }}>Start New Assessment</button>
                <Link to="/live-remediation" className="px-4 py-2 rounded-lg transition-colors hover:bg-primary/10" onClick={() => setMobileMenuOpen(false)}>Live Remediation</Link>
                <Link to="/analytics" className="px-4 py-2 rounded-lg transition-colors hover:bg-primary/10" onClick={() => setMobileMenuOpen(false)}>Analytics</Link>
                <div className="relative">
                  <Link to="/ai-trust-index" className="px-4 py-2 rounded-lg transition-colors relative hover:bg-primary/10" onClick={() => setMobileMenuOpen(false)}>
                    AI Maturity Index
                  </Link>
                </div>
                <div className="relative">
                  <Link to="/sentiment" className="px-4 py-2 rounded-lg transition-colors relative hover:bg-primary/10" onClick={() => setMobileMenuOpen(false)}>
                    AI News
                    <span className="inline-block ml-2 px-2 py-0.5 text-xs bg-warning text-background rounded-full text-[10px] font-medium align-middle">Coming Soon</span>
                  </Link>
                </div>
                <Link to="/sentiment" className="px-4 py-2 rounded-lg transition-colors relative hover:bg-primary/10" onClick={() => setMobileMenuOpen(false)}>
                  Sentiment Index
                  <span className="inline-block ml-2 px-2 py-0.5 text-xs bg-warning text-background rounded-full text-[10px] font-medium align-middle">Coming Soon</span>
                </Link>
              </motion.div>
            )}
          </AnimatePresence>
        </nav>
        {/* Main Content Area */}
        <main className="p-8">
          {children}
        </main>
      </div>
    </div>
  );
}

function AppRoutes() {
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    document.title = "Quantifaid - AI trust rating index";
  }, []);

  return (
    <Routes>
      {/* Auth Routes */}
      <Route path="/auth" element={user ? <Navigate to="/dashboard" replace /> : <Auth />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <MainLayout isWizardOpen={isWizardOpen} setIsWizardOpen={setIsWizardOpen}>
            <Dashboard />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/live-remediation" element={
        <ProtectedRoute>
          <MainLayout isWizardOpen={isWizardOpen} setIsWizardOpen={setIsWizardOpen}>
            <LiveRemediationActions />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute>
          <MainLayout isWizardOpen={isWizardOpen} setIsWizardOpen={setIsWizardOpen}>
            <Analytics />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/maturity" element={
        <ProtectedRoute>
          <MainLayout isWizardOpen={isWizardOpen} setIsWizardOpen={setIsWizardOpen}>
            <ComingSoon />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/sentiment" element={
        <ProtectedRoute>
          <MainLayout isWizardOpen={isWizardOpen} setIsWizardOpen={setIsWizardOpen}>
            <ComingSoon />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/ai-trust-index" element={
        <ProtectedRoute>
          <MainLayout isWizardOpen={isWizardOpen} setIsWizardOpen={setIsWizardOpen}>
            <AiTrustRatingIndex />
          </MainLayout>
        </ProtectedRoute>
      } />
      
      {/* Redirect root to auth if not logged in, or dashboard if logged in */}
      <Route path="/" element={<Navigate to={user ? "/dashboard" : "/auth"} replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <Toaster position="top-right" />
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;
